import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import jsQR from 'jsqr';
import { base44 } from '@/api/base44Client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Award,
  CalendarCheck,
  Camera,
  CheckCircle,
  Clock,
  Medal,
  QrCode,
  Shield,
  Sparkles,
  Trophy,
  Users,
  X
} from 'lucide-react';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { getMemberByUser } from '@/lib/memberLookup';
import {
  ACTIVE_QR_NAME,
  POINT_RULES,
  RANKING_TEAMS,
  buildRankingRows,
  findMemberTeams,
  findTodayScheduleForMember,
  findTodaySchedulesForMember,
  getCheckinTiming,
  loadRankingData,
  parseQrPayload,
  samePerson,
  validateCheckinLocation,
  validateCheckinTimeWindow
} from '@/lib/rankingMBV';
import { createPageUrl } from '@/utils';

const initials = (name) => String(name || 'MBV').split(' ').map((part) => part[0]).join('').slice(0, 2).toUpperCase();
const formatDate = (value) => {
  if (!value) return 'Sem data';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value).slice(0, 10);
  return format(parsed, "dd MMM yyyy 'às' HH:mm", { locale: ptBR });
};

const mapGeolocationError = (error) => {
  const withCode = (message) => {
    const nextError = new Error(message);
    nextError.code = error?.code;
    return nextError;
  };

  if (error?.code === error.PERMISSION_DENIED) {
    return withCode('Permita o acesso à localização para fazer check-in.');
  }
  if (error?.code === error.POSITION_UNAVAILABLE) {
    return withCode('Não foi possível obter sua localização agora. Ative o GPS e tente novamente.');
  }
  if (error?.code === error.TIMEOUT) {
    return withCode('Tempo esgotado ao buscar sua localização. Ative o GPS, aguarde alguns segundos e tente novamente.');
  }
  return withCode(error?.message || 'Não foi possível obter sua localização.');
};

const requestPosition = (options) => new Promise((resolve, reject) => {
  if (!navigator.geolocation) {
    reject(new Error('Localização indisponível neste dispositivo.'));
    return;
  }

  navigator.geolocation.getCurrentPosition(
    (position) => resolve({
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy
    }),
    reject,
    options
  );
});

const watchPositionUntilReady = (options = {}) => new Promise((resolve, reject) => {
  if (!navigator.geolocation) {
    reject(new Error('Localização indisponível neste dispositivo.'));
    return;
  }

  let settled = false;
  let bestPosition = null;
  let watchId = null;

  const finish = (callback, value) => {
    if (settled) return;
    settled = true;
    if (watchId != null) navigator.geolocation.clearWatch(watchId);
    callback(value);
  };

  const timeoutId = window.setTimeout(() => {
    if (bestPosition) {
      finish(resolve, bestPosition);
      return;
    }

    const timeoutError = new Error('Tempo esgotado ao buscar sua localização. Ative o GPS, aguarde alguns segundos e tente novamente.');
    timeoutError.code = 3;
    finish(reject, timeoutError);
  }, options.timeout || 90000);

  const clearAndFinish = (callback, value) => {
    window.clearTimeout(timeoutId);
    finish(callback, value);
  };

  watchId = navigator.geolocation.watchPosition(
    (position) => {
      const nextPosition = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy
      };

      if (!bestPosition || Number(nextPosition.accuracy || Infinity) < Number(bestPosition.accuracy || Infinity)) {
        bestPosition = nextPosition;
      }

      if (Number(nextPosition.accuracy || Infinity) <= 100) {
        clearAndFinish(resolve, nextPosition);
      }
    },
    (error) => clearAndFinish(reject, error),
    {
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: options.timeout || 90000
    }
  );
});

const getCurrentPosition = async () => {
  const attempts = [
    () => requestPosition({ enableHighAccuracy: true, timeout: 45000, maximumAge: 0 }),
    () => watchPositionUntilReady({ timeout: 90000 })
  ];

  let lastError = null;
  for (const attempt of attempts) {
    try {
      return await attempt();
    } catch (error) {
      lastError = error;
      if (error?.code === error.PERMISSION_DENIED) break;
    }
  }

  throw mapGeolocationError(lastError);
};

function Scanner({ onScan, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const stopCameraRef = useRef(() => {});
  const lastErrorRef = useRef('');
  const scanLockedRef = useRef(false);
  const [manualCode, setManualCode] = useState('');
  const [status, setStatus] = useState('Abrindo câmera...');
  const [scanSubmitted, setScanSubmitted] = useState(false);

  const submitScan = useCallback((value) => {
    const trimmedValue = String(value || '').trim();
    if (!trimmedValue || scanLockedRef.current) return;

    scanLockedRef.current = true;
    setScanSubmitted(true);
    setStatus('QR Code detectado. Validando check-in...');
    stopCameraRef.current?.();
    onScan(trimmedValue);
  }, [onScan]);

  useEffect(() => {
    let cancelled = false;
    let frameId = null;

    const stopCamera = () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      streamRef.current?.getTracks?.().forEach((track) => track.stop());
      streamRef.current = null;
    };
    stopCameraRef.current = stopCamera;

    const start = async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          setStatus('Câmera indisponível neste dispositivo. Digite o código do QR.');
          return;
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
          audio: false
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        videoRef.current.srcObject = stream;
        await videoRef.current.play();

        const detector = 'BarcodeDetector' in window
          ? new window.BarcodeDetector({ formats: ['qr_code'] })
          : null;
        const canvas = canvasRef.current;
        const context = canvas?.getContext('2d', { willReadFrequently: true });
        setStatus('Aponte para o QR Code MBV fixo.');

        const tick = async () => {
          if (!videoRef.current || cancelled) return;
          try {
            if (detector) {
              const codes = await detector.detect(videoRef.current);
              const value = codes?.[0]?.rawValue;
              if (value) {
                submitScan(value);
                return;
              }
            }

            const video = videoRef.current;
            if (context && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && video.videoWidth && video.videoHeight) {
              canvas.width = video.videoWidth;
              canvas.height = video.videoHeight;
              context.drawImage(video, 0, 0, canvas.width, canvas.height);
              const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
              const code = jsQR(imageData.data, imageData.width, imageData.height, {
                inversionAttempts: 'attemptBoth'
              });
              if (code?.data) {
                submitScan(code.data);
                return;
              }
            }
          } catch (error) {
            const message = error?.message || 'Não foi possível ler automaticamente. Digite o código do QR.';
            if (lastErrorRef.current !== message) {
              lastErrorRef.current = message;
              setStatus(message);
            }
          }
          frameId = window.requestAnimationFrame(tick);
        };

        frameId = window.requestAnimationFrame(tick);
      } catch (error) {
        setStatus(error?.message || 'Não foi possível abrir a câmera.');
      }
    };

    start();
    return () => {
      cancelled = true;
      stopCamera();
      stopCameraRef.current = () => {};
    };
  }, [submitScan]);

  return (
    <div className="space-y-4">
      <div className="relative overflow-hidden rounded-xl bg-slate-950 aspect-[4/3]">
        <video ref={videoRef} className="h-full w-full object-cover" playsInline muted />
        <canvas ref={canvasRef} className="hidden" />
        <div className="absolute inset-8 rounded-2xl border-2 border-white/80 shadow-[0_0_0_999px_rgba(15,23,42,0.45)]" />
      </div>
      <p className="text-sm text-gray-600">{status}</p>
      <div className="flex gap-2">
        <Input
          value={manualCode}
          onChange={(event) => setManualCode(event.target.value)}
          placeholder="Código manual do QR"
          disabled={scanSubmitted}
        />
        <Button onClick={() => submitScan(manualCode)} disabled={!manualCode.trim() || scanSubmitted}>
          Validar
        </Button>
      </div>
      <Button variant="outline" className="w-full" onClick={onClose}>
        <X className="w-4 h-4 mr-2" />
        Fechar
      </Button>
    </div>
  );
}

export default function RankingMBV() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const handledCheckinParamRef = useRef('');
  const scannerProcessingRef = useRef(false);
  const [activeTeamKey, setActiveTeamKey] = useState(RANKING_TEAMS[0].key);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerSessionKey, setScannerSessionKey] = useState(0);

  const { data: user, isLoading: loadingUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me()
  });

  const { data: member } = useQuery({
    queryKey: ['memberData', user?.id, user?.email],
    queryFn: () => getMemberByUser(user),
    enabled: !!user
  });

  const { data, isLoading } = useQuery({
    queryKey: ['rankingMBVData'],
    queryFn: loadRankingData
  });

  const memberTeams = useMemo(() => findMemberTeams(user, member, data?.teams), [user, member, data?.teams]);
  const allowedTeamKeys = useMemo(() => new Set(memberTeams.map((item) => item.team.key)), [memberTeams]);
  const visibleTeams = RANKING_TEAMS.filter((team) => allowedTeamKeys.has(team.key));

  useEffect(() => {
    if (visibleTeams.length > 0 && !allowedTeamKeys.has(activeTeamKey)) {
      setActiveTeamKey(visibleTeams[0].key);
    }
  }, [activeTeamKey, allowedTeamKeys, visibleTeams]);

  const activeTeam = RANKING_TEAMS.find((team) => team.key === activeTeamKey) || visibleTeams[0];
  const rankingByTeam = useMemo(() => {
    if (!data) return {};
    return RANKING_TEAMS.reduce((acc, team) => {
      const teamData = data.teams[team.key] || {};
      acc[team.key] = buildRankingRows({
        team,
        members: teamData.members || [],
        schedules: teamData.schedules || [],
        checkins: data.checkins || [],
        adjustments: data.adjustments || [],
        churchById: data.churchById
      });
      return acc;
    }, {});
  }, [data]);

  const myRows = useMemo(() => {
    return memberTeams.map(({ team, teamMember }) => {
      const rows = rankingByTeam[team.key] || [];
      const row = rows.find((item) => samePerson(teamMember, item));
      return { team, teamMember, row, position: rows.findIndex((item) => samePerson(teamMember, item)) + 1 };
    });
  }, [memberTeams, rankingByTeam]);

  const checkinMutation = useMutation({
    mutationFn: async (rawPayload) => {
      const payload = parseQrPayload(rawPayload);
      const activeQr = (data?.qrCodes || []).find((qr) => qr.name === ACTIVE_QR_NAME && qr.status === 'active');
      if (!activeQr?.token || payload.token !== activeQr.token) {
        throw new Error('QR Code inválido ou substituído pelo administrador.');
      }

      const checkedAt = new Date();
      const orderedMemberships = [
        ...memberTeams.filter((item) => item.team.key === activeTeamKey),
        ...memberTeams.filter((item) => item.team.key !== activeTeamKey)
      ];
      if (orderedMemberships.length === 0) {
        throw new Error('Apenas membros das equipes autorizadas podem fazer check-in.');
      }

      let selectedCheckin = null;
      let foundTodaySchedule = false;
      let foundPendingSchedule = false;
      let blockedByTimeMessage = '';

      for (const membership of orderedMemberships) {
        const teamData = data.teams[membership.team.key];
        const todaySchedules = findTodaySchedulesForMember(
          membership.team,
          teamData?.schedules || [],
          membership.teamMember,
          checkedAt
        );
        if (todaySchedules.length === 0) continue;
        foundTodaySchedule = true;

        for (const schedule of todaySchedules) {
          const alreadyChecked = (data.checkins || []).some((checkin) =>
            checkin.schedule_id === schedule.id &&
            checkin.team_key === membership.team.key &&
            samePerson(membership.teamMember, checkin)
          );
          if (alreadyChecked) continue;
          foundPendingSchedule = true;

          const timeValidation = validateCheckinTimeWindow(schedule, membership.team, checkedAt);
          if (!timeValidation.allowed) {
            blockedByTimeMessage = blockedByTimeMessage || timeValidation.message;
            continue;
          }

          selectedCheckin = { membership, schedule };
          break;
        }

        if (selectedCheckin) {
          break;
        }
      }

      if (!selectedCheckin) {
        if (!foundTodaySchedule) throw new Error('Nenhuma escala encontrada para você hoje nas equipes vinculadas.');
        if (!foundPendingSchedule) throw new Error('Check-in já realizado para a escala de hoje.');
        throw new Error(blockedByTimeMessage || 'Check-in indisponível para a escala de hoje.');
      }

      const { membership, schedule } = selectedCheckin;

      const currentPosition = await getCurrentPosition();
      const locationValidation = validateCheckinLocation(currentPosition, data?.churchInfo);
      if (!locationValidation.allowed) throw new Error(locationValidation.message);

      const timing = getCheckinTiming(schedule, membership.team, checkedAt);
      const record = await base44.entities.RankingMBVCheckin.create({
        qr_code_id: activeQr.id,
        qr_version: activeQr.version || 1,
        team_key: membership.team.key,
        team_label: membership.team.label,
        schedule_id: schedule.id,
        schedule_title: schedule.title || 'Escala',
        schedule_date: schedule.date,
        member_id: membership.teamMember.member_id || member?.id || null,
        team_member_id: membership.teamMember.id,
        email: membership.teamMember.email || membership.teamMember.member_email || user?.email || '',
        name: membership.teamMember.name || membership.teamMember.full_name || member?.full_name || user?.full_name,
        checked_at: checkedAt.toISOString(),
        timing_status: timing.status,
        timing_label: timing.label,
        points: timing.points,
        latitude: currentPosition.latitude,
        longitude: currentPosition.longitude,
        location_accuracy_meters: currentPosition.accuracy,
        distance_from_church_meters: locationValidation.distanceMeters,
        location_status: 'validated',
        location_warning: null,
        device_user_agent: navigator.userAgent
      });

      return { record, timing, schedule };
    },
    onSuccess: ({ timing }) => {
      toast.success(`Check-in confirmado: +${timing.points} MBV`);
      setScannerOpen(false);
      queryClient.invalidateQueries({ queryKey: ['rankingMBVData'] });
    },
    onError: (error) => toast.error(error?.message || 'Não foi possível fazer check-in.'),
    onSettled: () => {
      scannerProcessingRef.current = false;
      setScannerSessionKey((key) => key + 1);
    }
  });

  const handleScan = useCallback((value) => {
    if (scannerProcessingRef.current || checkinMutation.isPending) return;

    scannerProcessingRef.current = true;
    checkinMutation.mutate(value);
  }, [checkinMutation]);

  useEffect(() => {
    const checkinToken = searchParams.get('checkin') || searchParams.get('token');
    if (!checkinToken || isLoading || loadingUser || !data || memberTeams.length === 0) return;
    if (handledCheckinParamRef.current === checkinToken || checkinMutation.isPending) return;

    handledCheckinParamRef.current = checkinToken;
    checkinMutation.mutate(checkinToken);
    navigate(createPageUrl('RankingMBV'), { replace: true });
  }, [checkinMutation, data, isLoading, loadingUser, memberTeams.length, navigate, searchParams]);

  if (loadingUser || isLoading) {
    return <div className="min-h-screen bg-gray-50 p-4"><LoadingSpinner /></div>;
  }

  if (visibleTeams.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 pb-24">
        <div className="bg-slate-900 text-white pt-[calc(env(safe-area-inset-top)+1rem)] pb-10 px-4">
          <div className="max-w-4xl mx-auto">
            <PageHeader title="Ranking MBV" showBack={true} />
          </div>
        </div>
        <div className="max-w-lg mx-auto px-4 -mt-6">
          <div className="bg-white rounded-2xl shadow-sm p-6 text-center">
            <Shield className="w-12 h-12 mx-auto text-indigo-600 mb-3" />
            <h2 className="font-bold text-gray-900 text-xl">Acesso exclusivo das equipes</h2>
            <p className="text-gray-600 mt-2">Ranking MBV está disponível para Louvor, Mídia, Dança MBV, Recepção e Kids MBV.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <div className="bg-slate-900 text-white pt-[calc(env(safe-area-inset-top)+1rem)] pb-10 px-4">
        <div className="max-w-5xl mx-auto">
          <PageHeader title="Ranking MBV" subtitle="Pontuação, check-in e desempenho das equipes" showBack={true} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 mt-4">
            <div className="rounded-xl bg-slate-800 border border-slate-700 p-3 sm:p-4 min-w-0">
              <Trophy className="w-5 h-5 text-amber-300 mb-2" />
              <p className="text-xl sm:text-2xl font-bold">{myRows[0]?.row?.total || 0}</p>
              <p className="text-xs text-white/75">Seu total MBV</p>
            </div>
            <div className="rounded-xl bg-slate-800 border border-slate-700 p-3 sm:p-4 min-w-0">
              <Medal className="w-5 h-5 text-sky-200 mb-2" />
              <p className="text-xl sm:text-2xl font-bold">#{myRows[0]?.position || '-'}</p>
              <p className="text-xs text-white/75">Posição atual</p>
            </div>
            <div className="rounded-xl bg-slate-800 border border-slate-700 p-3 sm:p-4 min-w-0">
              <Users className="w-5 h-5 text-emerald-200 mb-2" />
              <p className="text-xl sm:text-2xl font-bold">{visibleTeams.length}</p>
              <p className="text-xs text-white/75">Equipes vinculadas</p>
            </div>
            <div className="rounded-xl bg-slate-800 border border-slate-700 p-3 sm:p-4 min-w-0">
              <Sparkles className="w-5 h-5 text-fuchsia-200 mb-2" />
              <p className="text-xl sm:text-2xl font-bold">{myRows.reduce((sum, item) => sum + (item.row?.checkinCount || 0), 0)}</p>
              <p className="text-xs text-white/75">Check-ins feitos</p>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 -mt-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="font-bold text-gray-900">Check-in da escala</h2>
              <p className="text-sm text-gray-500">Validação por QR Code fixo da igreja e escala do dia.</p>
            </div>
            <Button className="bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto" onClick={() => setScannerOpen(true)}>
              <QrCode className="w-4 h-4 mr-2" />
              Fazer Check-in
            </Button>
          </div>
          <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-5 gap-2 mt-4">
            <Badge variant="secondary" className="justify-center">Escalado +{POINT_RULES.scheduled}</Badge>
            <Badge variant="secondary" className="justify-center">Confirmar +{POINT_RULES.confirmed}</Badge>
            <Badge className="justify-center bg-emerald-600">Antes +{POINT_RULES.checkinEarly}</Badge>
            <Badge className="justify-center bg-indigo-600">No horário +{POINT_RULES.checkinOnTime}</Badge>
            <Badge className="justify-center bg-slate-600">Atraso +{POINT_RULES.checkinLate}</Badge>
          </div>
        </div>

        <Tabs value={activeTeamKey} onValueChange={setActiveTeamKey}>
          <TabsList className="w-full h-auto flex flex-wrap justify-start bg-white border border-gray-100 p-1 mb-4">
            {visibleTeams.map((team) => (
              <TabsTrigger key={team.key} value={team.key} className="flex-1 min-w-fit">
                {team.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {visibleTeams.map((team) => {
            const rows = rankingByTeam[team.key] || [];
            return (
              <TabsContent key={team.key} value={team.key} className="space-y-4">
                <div className="grid md:grid-cols-3 gap-3">
                  {rows.slice(0, 3).map((row, index) => (
                    <motion.div
                      key={row.id}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`rounded-2xl p-4 border shadow-sm ${index === 0 ? 'bg-amber-50 border-amber-200' : index === 1 ? 'bg-slate-50 border-slate-200' : 'bg-orange-50 border-orange-200'}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center font-bold text-gray-900">#{index + 1}</div>
                        <Avatar className="w-12 h-12">
                          <AvatarImage src={row.photo_url} />
                          <AvatarFallback>{initials(row.name)}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="font-bold text-gray-900 truncate">{row.name}</p>
                          <p className="text-sm text-gray-600">{row.total} MBV</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                  {rows.map((row, index) => (
                    <div key={row.id} className="flex items-center gap-3 p-4 border-b last:border-b-0">
                      <div className="w-9 text-center font-bold text-gray-500">#{index + 1}</div>
                      <Avatar className="w-11 h-11">
                        <AvatarImage src={row.photo_url} />
                        <AvatarFallback>{initials(row.name)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-gray-900 truncate">{row.name}</p>
                          {index < 3 && <Award className="w-4 h-4 text-amber-500 flex-shrink-0" />}
                        </div>
                        <p className="text-xs text-gray-500">{row.scheduledCount} escalas · {row.confirmedCount} confirmações · {row.checkinCount} check-ins</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg text-indigo-700">{row.total}</p>
                        <p className="text-xs text-gray-500">MBV</p>
                      </div>
                    </div>
                  ))}
                  {rows.length === 0 && <div className="p-6 text-center text-gray-500">Nenhum integrante nesta equipe.</div>}
                </div>
              </TabsContent>
            );
          })}
        </Tabs>

        <div className="grid md:grid-cols-2 gap-4 mt-5">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
            <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-3">
              <CalendarCheck className="w-5 h-5 text-indigo-600" />
              Próxima escala de hoje
            </h3>
            {memberTeams.map(({ team, teamMember }) => {
              const schedule = findTodayScheduleForMember(team, data.teams[team.key]?.schedules || [], teamMember);
              return (
                <div key={team.key} className="flex items-center justify-between py-2 border-b last:border-b-0">
                  <div>
                    <p className="font-medium text-gray-900">{team.label}</p>
                    <p className="text-sm text-gray-500">{schedule ? schedule.title : 'Sem escala hoje'}</p>
                  </div>
                  <Badge variant={schedule ? 'default' : 'secondary'}>{schedule?.[team.timeField] || schedule?.start_time || '-'}</Badge>
                </div>
              );
            })}
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
            <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-3">
              <Clock className="w-5 h-5 text-emerald-600" />
              Histórico recente
            </h3>
            {(rankingByTeam[activeTeam?.key] || [])
              .find((row) => myRows.some((item) => item.team.key === activeTeam?.key && samePerson(item.teamMember, row)))
              ?.history.slice(0, 6).map((item, index) => (
                <div key={`${item.type}-${index}`} className="flex items-start justify-between py-2 border-b last:border-b-0">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">{item.label}</p>
                    <p className="text-xs text-gray-500 truncate">{item.title} · {formatDate(item.date)}</p>
                  </div>
                  <Badge className={item.points >= 0 ? 'bg-emerald-600' : 'bg-red-600'}>{item.points > 0 ? '+' : ''}{item.points}</Badge>
                </div>
              )) || <p className="text-sm text-gray-500">Sem histórico nesta equipe.</p>}
          </div>
        </div>
      </main>

      <Dialog open={scannerOpen} onOpenChange={setScannerOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Fazer Check-in
            </DialogTitle>
          </DialogHeader>
          <Scanner
            key={scannerSessionKey}
            onScan={handleScan}
            onClose={() => setScannerOpen(false)}
          />
          {checkinMutation.isPending && (
            <div className="flex items-center gap-2 text-sm text-indigo-700">
              <CheckCircle className="w-4 h-4" />
              Validando QR Code e escala...
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
